import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
  Divider,
  Stack,
} from '@mui/material';
import { Save, Cancel, ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { request } from '../../../../../utils/request';
import { DailyLedger, FormDataLedger } from '../../../types';

const voucherOptions = [
  'Opening Balance',
  'Payment In',
  'Sales Invoice',
  'Sales Return',
  'Credit Note',
  'Other'
];

const AddEditDailyLedger: React.FC = () => {
  const { partyId, ledgerId } = useParams<{ partyId: string; ledgerId?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(ledgerId);
  const [showCustomVoucher, setShowCustomVoucher] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataLedger>({
    date: new Date().toISOString().split('T')[0],
    voucher: '',
    voucherCustom: '',
    credit: '',
    debit: '',
    description: '',
    status: 'active'
  });

  const fetchLedgerData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await request({
        url: `dailyLedger/get-single-ledger/${ledgerId}`,
        method: 'get',
      });

      if (response.success) {
        const ledger: DailyLedger = response.data;
        // Check if voucher is in predefined options
        const isPredefinedOption = voucherOptions.includes(ledger.voucher || '');

        setFormData({
          date: ledger.date || '',
          voucher: isPredefinedOption ? (ledger.voucher || '') : 'Other',
          voucherCustom: !isPredefinedOption ? (ledger.voucher || '') : '',
          credit: ledger.credit?.toString() || '',
          debit: ledger.debit?.toString() || '',
          description: ledger.description || '',
          status: ledger.status
        });

        // Show custom field if voucher is not in predefined options
        setShowCustomVoucher(!isPredefinedOption);
      } else {
        toast.error(response.message || 'Failed to fetch ledger data');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch ledger data');
      console.error('Error fetching ledger:', error);
    } finally {
      setLoading(false);
    }
  }, [ledgerId])

  // Fetch ledger data for edit
  useEffect(() => {
    if (isEdit && ledgerId) {
      fetchLedgerData();
    }
  }, [isEdit, ledgerId, fetchLedgerData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>): void => {
    const { name, value } = e.target;

    if (name === 'voucher') {
      const isOther = value === 'Other';
      setShowCustomVoucher(isOther);

      // Reset custom voucher when switching to predefined option
      if (!isOther) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          voucherCustom: ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDebitChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFormData(prev => ({
        ...prev,
        debit: value,
        credit: value ? '' : prev.credit // Clear credit if debit has value
      }));
    }
  };

  const handleCreditChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFormData(prev => ({
        ...prev,
        credit: value,
        debit: value ? '' : prev.debit // Clear debit if credit has value
      }));
    }
  };

  const validateForm = (): boolean => {
    if (!formData.date) {
      toast.error('Date is required');
      return false;
    }

    // Check if "Other" is selected but custom voucher is empty
    if (formData.voucher === 'Other' && !formData.voucherCustom.trim()) {
      toast.error('Please enter custom voucher type');
      return false;
    }

    // Check if at least one of credit or debit has value
    if (!formData.credit && !formData.debit) {
      toast.error('Either Credit or Debit amount is required');
      return false;
    }

    // Check if both credit and debit have values (this should not happen with new logic)
    if (formData.credit && formData.debit) {
      toast.error('Cannot have both Credit and Debit amounts');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validateForm() || !partyId) return;

    setLoading(true);
    try {
      // Determine final voucher value
      const finalVoucher = formData.voucher === 'Other'
        ? formData.voucherCustom
        : formData.voucher;

      const payload = {
        partyId,
        date: formData.date,
        voucher: finalVoucher || undefined,
        credit: formData.credit ? parseFloat(formData.credit) : undefined,
        debit: formData.debit ? parseFloat(formData.debit) : undefined,
        description: formData.description ? formData.description : undefined,
        status: formData.status
      };

      const url = isEdit
        ? `dailyLedger/update-ledger/${ledgerId}`
        : 'dailyLedger/add-ledger';

      const method = isEdit ? 'put' : 'post';

      const response = await request({
        url,
        method,
        data: payload
      });

      if (response.success) {
        toast.success(response.message || `Ledger ${isEdit ? 'updated' : 'added'} successfully`);
        navigate(`/parties/dailyLedger/${partyId}`);
      } else {
        toast.error(response.message || `Failed to ${isEdit ? 'update' : 'add'} ledger`);
      }
    } catch (error: any) {
      toast.error(error?.message || `Failed to ${isEdit ? 'update' : 'add'} ledger`);
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (): void => {
    navigate(`/parties/dailyLedger/${partyId}`);
  };

  const formatCurrencyDisplay = (value: string): string => {
    if (!value) return '';
    const number = parseFloat(value);
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  return (
    <Box sx={{ margin: '0 auto' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleCancel}
            size="small"
            sx={{ minWidth: 'auto' }}
          >
            Back
          </Button>
          <Typography variant="h5" component="h1" fontWeight="bold">
            {isEdit ? 'Edit' : 'Add'} Daily Ledger
          </Typography>
        </Box>
      </Box>

      <Card sx={{ boxShadow: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {/* Basic Information Section */}
              <Paper sx={{ p: 2, backgroundColor: "#f6f8fb" }} elevation={0} variant="outlined">
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" gap={2} flexDirection="column">
                  {/* First Row: Date and Voucher Type */}
                  <Box display="flex" gap={2}>
                    <TextField
                      label="Date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <FormControl fullWidth size="small">
                      <InputLabel>Voucher Type</InputLabel>
                      <Select
                        name="voucher"
                        value={formData.voucher}
                        label="Voucher Type"
                        onChange={handleSelectChange}
                      >
                        {voucherOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Custom Voucher Field (shown when "Other" is selected) */}
                  {showCustomVoucher && (
                    <Box display="flex" gap={2}>
                      <TextField
                        label="Custom Voucher"
                        name="voucherCustom"
                        value={formData.voucherCustom}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        placeholder="Enter custom voucher type"
                        required
                      />
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Amount Information Section */}
              <Paper sx={{ p: 2, backgroundColor: "#f6f8fb" }} elevation={0} variant="outlined">
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Amount Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                      label="Debit Amount (₹)"
                      name="debit"
                      value={formData.debit}
                      onChange={handleDebitChange}
                      fullWidth
                      size="small"
                      placeholder="0.00"
                      InputProps={{
                        endAdornment: formData.debit && (
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                            {formatCurrencyDisplay(formData.debit)}
                          </Typography>
                        )
                      }}
                    />

                    <TextField
                      label="Credit Amount (₹)"
                      name="credit"
                      value={formData.credit}
                      onChange={handleCreditChange}
                      fullWidth
                      size="small"
                      placeholder="0.00"
                      InputProps={{
                        endAdornment: formData.credit && (
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                            {formatCurrencyDisplay(formData.credit)}
                          </Typography>
                        )
                      }}
                    />
                  </Box>
                </Stack>
              </Paper>

              {/* Status Section */}
              <Paper sx={{ p: 2, backgroundColor: "#f6f8fb" }} elevation={0} variant="outlined">
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Description & Status
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    size="small"
                    placeholder="Description..."
                    InputProps={{
                      endAdornment: formData.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                          {formData.description}
                        </Typography>
                      )
                    }}
                    sx={{
                      width: "80%"
                    }}
                  />

                  <FormControl size="small" sx={{ width: "20%" }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      label="Status"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Paper>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', pt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={loading}
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={loading}
                  size="small"
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update' : 'Save')}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddEditDailyLedger;